import { useEffect, useState } from "react";
import { Loader2, Truck, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { UserAvatar } from "@/components/UserBits";
import { MysteryOverview } from "@/components/MysteryOverview";

const LABEL = { address: "Wartet auf Adresse", preparing: "Bereit zum Versand", shipped: "Versendet", delivered: "Angekommen", forfeited: "Verfallen" };
const COLOR = { address: "bg-slate-100 text-slate-600", preparing: "bg-amber-100 text-amber-700", shipped: "bg-sky-100 text-sky-700", delivered: "bg-emerald-100 text-emerald-700", forfeited: "bg-stone-200 text-stone-500" };
const FILTERS = [["open", "Offen"], ["preparing", "Zu versenden"], ["shipped", "Versendet"], ["delivered", "Angekommen"], ["forfeited", "Verfallen"], ["all", "Alle"]];

function Row({ b, onSaved }) {
  const [tracking, setTracking] = useState(b.tracking || "");
  const [busy, setBusy] = useState(false);
  const set = async (status) => {
    setBusy(true);
    try { await api.put(`/admin/mystery-boxes/${b.id}`, { status, tracking }); toast.success(status === "shipped" ? "Als versendet markiert – Nutzer bekommt eine Push" : "Zurückgesetzt"); onSaved(); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  const a = b.address;
  return (
    <li className="bg-white rounded-2xl border border-pink-100 p-4 space-y-3" data-testid={`mystery-admin-${b.id}`}>
      <div className="flex items-center gap-3">
        <UserAvatar userId={b.user.user_id} name={b.user.name} picture={b.user.picture} size="w-10 h-10" />
        <div className="min-w-0 flex-1"><p className="font-bold truncate">{b.user.name} · Box #{b.n}</p><p className="text-xs text-slate-500 truncate">{b.user.email} · freigeschaltet {new Date(b.earned_at).toLocaleDateString("de-DE")}</p></div>
        <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${COLOR[b.status]}`} data-testid={`mystery-admin-status-${b.id}`}>{LABEL[b.status]}</span>
      </div>
      {a && <p className="text-sm text-slate-700 bg-rose-50/60 rounded-xl px-3 py-2 whitespace-pre-line" data-testid={`mystery-admin-address-${b.id}`}>{`${a.full_name}\n${a.street}\n${a.zip} ${a.city}\n${a.country}`}</p>}
      {(b.status === "preparing" || b.status === "shipped") && (
        <div className="flex flex-wrap gap-2">
          <input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="Sendungsnummer (optional)" data-testid={`mystery-admin-tracking-${b.id}`} className="flex-1 min-w-[180px] h-10 px-3 rounded-xl border border-pink-200 text-sm" />
          <button onClick={() => set("shipped")} disabled={busy} data-testid={`mystery-admin-ship-${b.id}`} className="h-10 px-4 rounded-full bg-rose-500 text-white text-sm font-bold inline-flex items-center gap-1.5 disabled:opacity-50"><Truck className="w-4 h-4" /> {b.status === "shipped" ? "Nummer speichern" : "Als versendet markieren"}</button>
          {b.status === "shipped" && <button onClick={() => set("preparing")} disabled={busy} data-testid={`mystery-admin-undo-${b.id}`} className="h-10 px-4 rounded-full border border-pink-200 text-sm font-bold text-slate-600 inline-flex items-center gap-1.5"><RotateCcw className="w-4 h-4" /> Zurücksetzen</button>}
        </div>
      )}
    </li>
  );
}

export function MysteryAdmin() {
  const [list, setList] = useState(null);
  const [f, setF] = useState("open");
  const load = () => api.get("/admin/mystery-boxes").then(({ data }) => setList(data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  if (!list) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-rose-500" /></div>;
  const shown = list.filter((b) => f === "all" || (f === "open" ? !["delivered", "forfeited"].includes(b.status) : b.status === f));
  return (
    <div className="space-y-4" data-testid="mystery-admin">
      <MysteryOverview />
      <h3 className="font-bold pt-2">Versandliste</h3>
      <div className="flex flex-wrap gap-2">
        {FILTERS.map(([k, l]) => <button key={k} onClick={() => setF(k)} data-testid={`mystery-admin-filter-${k}`} className={`h-9 px-4 rounded-full text-sm font-bold ${f === k ? "bg-rose-500 text-white" : "bg-white border border-pink-200 text-slate-600"}`}>{l} ({list.filter((b) => k === "all" || (k === "open" ? !["delivered", "forfeited"].includes(b.status) : b.status === k)).length})</button>)}
      </div>
      {shown.length === 0 ? <p className="text-sm text-slate-500" data-testid="mystery-admin-empty">Keine Boxen in dieser Ansicht.</p> : <ul className="space-y-3">{shown.map((b) => <Row key={b.id} b={b} onSaved={load} />)}</ul>}
    </div>
  );
}
